/**
 * Water: coastlines, lakes, drainage and rivers.
 *
 * The interesting problem here is that a noise-derived landscape is full of closed
 * basins — pits with no downhill exit. A river started above one flows in and stops,
 * so a naive generator produces stubby rivers that end nowhere. The fix is a
 * depression-filling pass (priority flood) that raises each basin to the height of
 * its lowest spill point, after which *every* land tile has a path downhill to the
 * sea. Rivers then trace trivially and always arrive somewhere.
 */

import { shuffle, type RngState } from '../rng/index.js';
import type { TileGrid } from '../state/types.js';
import { neighborsByDirection, oppositeDirection } from '../state/grid.js';
import { MinHeap } from '../util/heap.js';
import type { MapgenParams } from './params.js';

/** Reusable neighbour buffer. Map generation is single-threaded and sequential. */
const NEIGHBOURS = new Int32Array(6);

export function buildLandMask(height: Float32Array, seaLevel: number): Uint8Array {
  const mask = new Uint8Array(height.length);
  for (let i = 0; i < height.length; i++) mask[i] = height[i]! > seaLevel ? 1 : 0;
  return mask;
}

/**
 * Flood-fill connected regions of tiles matching `wanted`, returning a region id per
 * tile (-1 where it does not match) and each region's size.
 *
 * Iterative with an explicit stack: a recursive fill overflows on a 12,600-tile
 * continent, and it would do so only on large maps, which is the worst way to find out.
 */
export function findRegions(
  grid: TileGrid,
  mask: Uint8Array,
  wanted: number,
): { region: Int32Array; sizes: number[] } {
  const tiles = mask.length;
  const region = new Int32Array(tiles).fill(-1);
  const sizes: number[] = [];
  const stack: number[] = [];

  for (let start = 0; start < tiles; start++) {
    if (mask[start] !== wanted || region[start] !== -1) continue;

    const id = sizes.length;
    let size = 0;
    stack.length = 0;
    stack.push(start);
    region[start] = id;

    while (stack.length > 0) {
      const current = stack.pop()!;
      size++;
      neighborsByDirection(grid, current, NEIGHBOURS);
      for (let d = 0; d < 6; d++) {
        const n = NEIGHBOURS[d]!;
        if (n === -1 || region[n] !== -1 || mask[n] !== wanted) continue;
        region[n] = id;
        stack.push(n);
      }
    }
    sizes.push(size);
  }

  return { region, sizes };
}

/**
 * Drown land regions below `minSize`. Single-tile specks left by noise are visual
 * litter and, worse, unusable city sites the AI will still evaluate.
 * Returns how many tiles were removed.
 */
export function removeSmallIslands(grid: TileGrid, isLand: Uint8Array, minSize: number): number {
  if (minSize <= 1) return 0;
  const { region, sizes } = findRegions(grid, isLand, 1);
  let drowned = 0;
  for (let i = 0; i < isLand.length; i++) {
    const id = region[i]!;
    if (id !== -1 && sizes[id]! < minSize) {
      isLand[i] = 0;
      drowned++;
    }
  }
  return drowned;
}

export interface WaterBodies {
  /** Enclosed water bodies small enough to be lakes. */
  isLake: Uint8Array;
  /** Water tiles adjacent to land — shallow water, and the sea a city can build a harbour on. */
  isCoast: Uint8Array;
  lakeCount: number;
}

/**
 * Classify water into ocean, coast and lake.
 *
 * A water body that does not touch the largest body is enclosed. Small ones become
 * lakes (fresh water, high food); large ones stay ocean, which is how inland seas
 * arise — they are geographically interesting and cost nothing to allow.
 */
export function classifyWaterBodies(grid: TileGrid, isLand: Uint8Array, params: MapgenParams): WaterBodies {
  const tiles = isLand.length;
  const isLake = new Uint8Array(tiles);
  const isCoast = new Uint8Array(tiles);

  const { region, sizes } = findRegions(grid, isLand, 0);

  // The largest water body is the ocean by definition.
  let ocean = -1;
  let largest = -1;
  for (let id = 0; id < sizes.length; id++) {
    if (sizes[id]! > largest) {
      largest = sizes[id]!;
      ocean = id;
    }
  }

  let lakeCount = 0;
  const lakeRegions = new Set<number>();
  for (let id = 0; id < sizes.length; id++) {
    if (id !== ocean && sizes[id]! <= params.maxLakeSize) {
      lakeRegions.add(id);
      lakeCount++;
    }
  }

  for (let i = 0; i < tiles; i++) {
    if (isLand[i] === 1) continue;
    if (lakeRegions.has(region[i]!)) {
      isLake[i] = 1;
      continue;
    }
    neighborsByDirection(grid, i, NEIGHBOURS);
    for (let d = 0; d < 6; d++) {
      const n = NEIGHBOURS[d]!;
      if (n !== -1 && isLand[n] === 1) {
        isCoast[i] = 1;
        break;
      }
    }
  }

  return { isLake, isCoast, lakeCount };
}

/**
 * Priority flood (Barnes, Lehman & Mulla): raise every closed basin to its spill
 * height, so water can always run downhill to the sea.
 *
 * Seed the queue with all water tiles, then repeatedly take the lowest tile seen so
 * far and push its unvisited neighbours at `max(their height, this height)`. Because
 * the queue always yields the lowest available tile, a neighbour's filled height is
 * exactly the lowest barrier between it and the ocean.
 *
 * Returns a new height field; the original is left untouched, since terrain
 * assignment wants real elevations, not filled ones.
 */
export function fillDepressions(
  grid: TileGrid,
  height: Float32Array,
  isLand: Uint8Array,
  epsilon = 1e-6,
): Float32Array {
  const tiles = height.length;
  const filled = new Float32Array(tiles);
  const visited = new Uint8Array(tiles);
  const heap = new MinHeap(Math.max(64, Math.floor(tiles / 4)));

  for (let i = 0; i < tiles; i++) {
    filled[i] = height[i]!;
    if (isLand[i] === 0) {
      visited[i] = 1;
      heap.push(i, height[i]!);
    }
  }

  // A map with no water at all (possible only with an extreme sea level) would leave
  // the heap empty; seeding from the lowest land tile keeps the algorithm total.
  if (heap.isEmpty) {
    let lowest = 0;
    for (let i = 1; i < tiles; i++) if (height[i]! < height[lowest]!) lowest = i;
    visited[lowest] = 1;
    heap.push(lowest, height[lowest]!);
  }

  while (!heap.isEmpty) {
    const current = heap.pop()!;
    const currentHeight = filled[current]!;
    neighborsByDirection(grid, current, NEIGHBOURS);
    for (let d = 0; d < 6; d++) {
      const n = NEIGHBOURS[d]!;
      if (n === -1 || visited[n] === 1) continue;
      visited[n] = 1;
      // Raise the neighbour to just above the barrier we arrived over. The epsilon
      // matters: filling a basin exactly flat leaves a plateau with no downhill
      // direction, and a river entering it would stop dead. A minute upward slope
      // along the flood order guarantees every tile still drains somewhere.
      filled[n] = Math.max(filled[n]!, currentHeight + epsilon);
      heap.push(n, filled[n]!);
    }
  }

  return filled;
}

export interface RiverResult {
  /** Six-bit mask per tile: which neighbours this tile's river continues to. 0 = no river. */
  rivers: Uint8Array;
  riverCount: number;
  riverTiles: number;
}

/**
 * Trace rivers from wet highlands down to water.
 *
 * Sources are drawn from the wettest, highest land, which is where rivers actually
 * start. Each traces downhill across the depression-filled surface, so it always
 * reaches the sea, a lake, or an existing river. Runs that end too short are
 * discarded rather than committed — a two-tile river is noise, not geography.
 *
 * The stored mask records which neighbours a river continues to, so the renderer can
 * draw a connected course and the rules can ask "does this tile have fresh water?"
 * and "are these two tiles connected by river?" without a second structure.
 */
export function traceRivers(
  grid: TileGrid,
  filled: Float32Array,
  isLand: Uint8Array,
  moisture: Float32Array,
  landHeightThreshold: number,
  params: MapgenParams,
  rng: RngState,
): RiverResult {
  const tiles = isLand.length;
  const rivers = new Uint8Array(tiles);

  // Candidate sources: land, high enough to be upland, and wet enough to feed a river.
  const candidates: number[] = [];
  for (let i = 0; i < tiles; i++) {
    if (isLand[i] === 1 && filled[i]! >= landHeightThreshold && moisture[i]! > params.desertMoisture) {
      candidates.push(i);
    }
  }
  shuffle(rng, candidates);

  let landTiles = 0;
  for (let i = 0; i < tiles; i++) if (isLand[i] === 1) landTiles++;
  const target = Math.round(landTiles * params.riverDensity);

  // A river longer than the map is half wide is almost certainly following an
  // artefact rather than a valley, so cap the trace rather than let it wander.
  const maxSteps = Math.floor(Math.min(grid.width, grid.height) * 1.2);
  let riverCount = 0;

  for (const source of candidates) {
    if (riverCount >= target) break;
    if (rivers[source] !== 0) continue; // already part of a river

    // Walk downhill, recording the direction taken at each step.
    const path: number[] = [source];
    const directions: number[] = [];
    let current = source;
    let reachedWater = false;
    let joinedRiver = false;

    for (let step = 0; step < maxSteps; step++) {
      neighborsByDirection(grid, current, NEIGHBOURS);

      // Steepest descent. Ties break by lower tile index so that identical heights —
      // which do occur on filled flats — always resolve the same way, keeping
      // generation reproducible.
      let bestDirection = -1;
      let bestIndex = -1;
      let bestHeight = filled[current]!;
      for (let d = 0; d < 6; d++) {
        const n = NEIGHBOURS[d]!;
        if (n === -1) continue;
        const h = filled[n]!;
        if (bestIndex === -1) {
          if (h < bestHeight) {
            bestHeight = h;
            bestDirection = d;
            bestIndex = n;
          }
        } else if (h < bestHeight || (h === bestHeight && n < bestIndex)) {
          bestHeight = h;
          bestDirection = d;
          bestIndex = n;
        }
      }
      if (bestDirection === -1) break; // a true local minimum; depression filling makes this rare

      const next = NEIGHBOURS[bestDirection]!;
      directions.push(bestDirection);
      path.push(next);

      if (isLand[next] === 0) {
        reachedWater = true;
        break;
      }
      if (rivers[next] !== 0) {
        joinedRiver = true;
        break;
      }
      current = next;
    }

    const longEnough = directions.length >= params.riverMinLength;
    if (!longEnough || !(reachedWater || joinedRiver)) continue;

    // Commit: mark both ends of every edge the course runs along.
    for (let step = 0; step < directions.length; step++) {
      const from = path[step]!;
      const to = path[step + 1]!;
      const direction = directions[step]!;
      rivers[from] = (rivers[from]! | (1 << direction)) & 0x3f;
      rivers[to] = (rivers[to]! | (1 << oppositeDirection(direction))) & 0x3f;
    }
    riverCount++;
  }

  let riverTiles = 0;
  for (let i = 0; i < tiles; i++) if (rivers[i] !== 0 && isLand[i] === 1) riverTiles++;

  return { rivers, riverCount, riverTiles };
}

/** True when a tile touches fresh water — its own river, or an adjacent river or lake. */
export function hasFreshWater(grid: TileGrid, index: number, rivers: Uint8Array, isLake: Uint8Array): boolean {
  if (rivers[index] !== 0 || isLake[index] === 1) return true;
  neighborsByDirection(grid, index, NEIGHBOURS);
  for (let d = 0; d < 6; d++) {
    const n = NEIGHBOURS[d]!;
    if (n !== -1 && (isLake[n] === 1 || rivers[n] !== 0)) return true;
  }
  return false;
}

/**
 * How far out to sea you can get: the hex distance from each water tile to the
 * nearest land, by multi-source breadth-first search from every coastal tile.
 *
 * This exists because "the water feels concentrated in one place" is a real and
 * common complaint about generated maps, and it needs to be a number before it can
 * be fixed. A map whose land is spread out has a low maximum — there is always a
 * coast within a few tiles. A map with one dead void has a high maximum, however
 * good its overall land-to-water ratio looks.
 *
 * Note that some open ocean is *wanted*: it is what makes seafaring and exploration
 * matter. What is not wanted is one enormous emptiness with all the land crowded
 * into the remainder.
 */
export function seaDistances(grid: TileGrid, isLand: Uint8Array): Int32Array {
  const tiles = isLand.length;
  const distance = new Int32Array(tiles).fill(-1);
  // A ring buffer sized to the map: BFS visits each tile at most once.
  const queue = new Int32Array(tiles);
  let head = 0;
  let tail = 0;

  for (let i = 0; i < tiles; i++) {
    if (isLand[i] === 1) {
      distance[i] = 0;
      queue[tail++] = i;
    }
  }

  while (head < tail) {
    const current = queue[head++]!;
    const next = distance[current]! + 1;
    neighborsByDirection(grid, current, NEIGHBOURS);
    for (let d = 0; d < 6; d++) {
      const n = NEIGHBOURS[d]!;
      if (n === -1 || distance[n] !== -1) continue;
      distance[n] = next;
      queue[tail++] = n;
    }
  }

  return distance;
}

export interface OpenWaterStats {
  /** The furthest any tile gets from land. The emptiest point of the emptiest ocean. */
  maxSeaDistance: number;
  /** Mean distance to land over water tiles — how open the seas are on average. */
  meanSeaDistance: number;
  /** Share of the whole map more than `deep` tiles from any land. */
  deepWaterShare: number;
}

export function openWaterStats(grid: TileGrid, isLand: Uint8Array, deep = 5): OpenWaterStats {
  const distance = seaDistances(grid, isLand);
  let max = 0;
  let total = 0;
  let water = 0;
  let deepTiles = 0;

  for (let i = 0; i < distance.length; i++) {
    if (isLand[i] === 1) continue;
    const d = distance[i]!;
    water++;
    total += d;
    if (d > max) max = d;
    if (d > deep) deepTiles++;
  }

  return {
    maxSeaDistance: max,
    meanSeaDistance: water === 0 ? 0 : total / water,
    deepWaterShare: deepTiles / distance.length,
  };
}
